import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'freedom-of-information-vs-subject-access-request';

export const FoiVsSar = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        These two get mixed up constantly, including sometimes by the organisations meant to
        handle them, and it matters which one you actually send — the wrong one can get you a
        legally correct but useless answer. A Freedom of Information (FOI) request and a Subject
        Access Request (SAR) are different rights, under different laws, answering different
        questions. If you want your own care, school, health or police records, you want a SAR,
        almost always — but it is worth knowing exactly why, and what the other one is actually
        for.
      </p>
    </GuideSection>

    <GuideSection eyebrow="The short version" title="One is about you. The other isn't.">
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">A Subject Access Request</strong> is about{' '}
            <strong className="text-ink">your own personal data</strong> — information that
            relates to you specifically. Only you (or someone you authorise) can make one for
            yourself, under Article 15 of the UK GDPR. See our{' '}
            <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
              plain-English guide to SARs
            </Link>{' '}
            for the full mechanics.
          </>,
          <>
            <strong className="text-ink">A Freedom of Information request</strong> is about
            information a public authority holds{' '}
            <strong className="text-ink">generally</strong> — policies, decisions, statistics,
            correspondence, budgets. Under{' '}
            <SourceLink href="https://www.legislation.gov.uk/ukpga/2000/36/section/1">
              section 1 of the Freedom of Information Act 2000
            </SourceLink>
            , "any person" can ask, for any reason or none, and it has nothing to do with whether
            the information is about them.
          </>,
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Why FOI won't get you your own file" title="Your own data is specifically excluded from FOI">
      <p>
        This is not a grey area. The Act says so directly:{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2000/36/section/40">
          section 40(1)
        </SourceLink>{' '}
        makes any information "exempt information" from an FOI request if it "constitutes
        personal data of which the applicant is the data subject" — in other words, the moment
        you ask a council for information about yourself under FOI, it stops being an FOI matter
        by law, because that is exactly what the separate SAR right exists for. An organisation
        that gets an FOI request for someone&apos;s own care file should redirect it to be handled
        as a SAR, not attempt to answer it as FOI and then apply FOI&apos;s exemptions to it.
      </p>
    </GuideSection>

    <GuideNote>
      <p>
        <strong>This is also where a common redaction mix-up comes from.</strong> Section 40 of
        the Freedom of Information Act is the reason a third party&apos;s personal data can be
        withheld from an <em>FOI</em> response — a completely different provision from the one
        that actually governs redacting third-party information out of a <em>SAR</em> response,
        which is{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/schedule/2/paragraph/16">
          Schedule 2, paragraph 16 of the Data Protection Act 2018
        </SourceLink>
        . Staff who mostly handle FOI sometimes reach for "section 40" out of habit when
        redacting a SAR reply too — it is worth knowing that is the wrong citation if you ever see
        it, since the actual rules aren&apos;t identical. See our{' '}
        <Link to="/guides/understanding-redactions-in-your-care-records" className="text-brand underline hover:opacity-90">
          guide to redactions
        </Link>{' '}
        for how SAR redactions actually work.
      </p>
    </GuideNote>

    <GuideSection eyebrow="Who each one applies to" title="FOI only reaches public authorities">
      <p>
        A SAR applies to almost anyone processing your data — a private company, a charity, an
        independent fostering agency, as much as a council. FOI is narrower: it only applies to{' '}
        <strong className="text-ink">public authorities</strong>, a specific list set out in{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2000/36/schedule/1">
          Schedule 1 of the Act
        </SourceLink>
        — councils, government departments, the NHS, the police, and, somewhat surprisingly,{' '}
        <strong className="text-ink">both maintained schools and academies</strong> are listed.
        What is not covered: independent fostering agencies, private children&apos;s homes, and
        other private or voluntary organisations — bodies your SAR right still reaches, just not
        an FOI request.
      </p>
    </GuideSection>

    <GuideCTA />

    <GuideSection eyebrow="Practical differences" title="Deadlines, cost and what you actually get">
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">Timescale.</strong> An FOI request must be answered
            within{' '}
            <SourceLink href="https://www.legislation.gov.uk/ukpga/2000/36/section/10">
              20 working days
            </SourceLink>{' '}
            — not calendar days, and with no ordinary extension for complexity. A SAR has one
            calendar month, extendable by up to two further months for a genuinely complex
            request.
          </>,
          'Cost: both are free to make. Both can, in narrow circumstances, attract a fee or be refused as excessive — but a normal request for your own care records is neither.',
          "What you get: FOI gives you recorded information as it exists, with a long list of its own exemptions built around public interest (national security, commercial sensitivity, policy formation). A SAR gives you your personal data specifically, with a narrower, different set of exemptions built around protecting other people's privacy and specific sensitive categories like health and social work data.",
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="foi-vs-sar-faq">
        <GuideFaqItem
          q="I want to know a council's general policy on something, not my own file. Which do I use?"
          a="FOI. If you want to know a council's general approach — how many children were in care in a given year, what its policy on respite carers is — that's exactly what Freedom of Information is for, and you don't need to be a care leaver or have any connection to the council to ask."
        />
        <GuideFaqItem
          q="Can I use FOI to find out things about myself that a SAR redacted?"
          a="No — the same information doesn't become more available just because you ask under a different law. If it's your personal data, section 40(1) exempts it from FOI outright, and you're back to the SAR route and its own rules for challenging a redaction."
        />
        <GuideFaqItem
          q="What about Environmental Information Regulations (EIR)?"
          a="EIR is a cousin of FOI for environmental information specifically — air quality, planning, pollution — and works in a broadly similar way. It's very unlikely to be relevant to requesting your own care records, so we won't cover it here."
        />
        <GuideFaqItem
          q="Does an academy count as a public authority for FOI, even though it isn't run by the council?"
          a="Yes — academies and academy trusts are specifically listed in the Act's Schedule 1, alongside maintained schools. That's about general FOI requests to them, though; for your own school records specifically, you still want a SAR, not FOI, for the reason above."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The general right and its personal-data exemption are in sections{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2000/36/section/1">1</SourceLink>{' '}
        and{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2000/36/section/40">40</SourceLink>{' '}
        of the{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2000/36/contents">
          Freedom of Information Act 2000
        </SourceLink>
        , with the list of bodies it covers in{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2000/36/schedule/1">
          Schedule 1
        </SourceLink>
        . For the SAR side, see our{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          plain-English guide to SARs
        </Link>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="We only ever send the right kind of request"
      body="Precious Data drafts and sends Subject Access Requests, not Freedom of Information requests — the one built for getting your own personal records, with your own deadlines and your own rights behind it."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default FoiVsSar;
